import { defineMessages, useIntl } from 'react-intl';

import type { List } from 'immutable';

import type { CustomEmoji } from '../models/custom_emoji';
import type { Status } from '../models/status';

import { EmojiHTML } from './emoji/html';
import type { IconName } from './media_icon';
import { MediaIcon } from './media_icon';
import { StatusBanner, BannerVariant } from './status_banner';

const messages = defineMessages({
  mfmPhotosensitiveWarning: {
    id: 'status.mfm_photosensitive_warning',
    defaultMessage: '⚠ Contains photosensitive effects (MFM)',
  },
});

export const ContentWarning: React.FC<{
  status: Status;
  expanded?: boolean;
  onClick?: () => void;
  icons?: IconName[];
  mfmAutoHidden?: boolean;
}> = ({ status, expanded, onClick, icons, mfmAutoHidden }) => {
  const intl = useIntl();
  const hasSpoiler = !!status.get('spoiler_text');

  // Show MFM photosensitive warning even when there is no explicit spoiler text
  if (!hasSpoiler && mfmAutoHidden) {
    return (
      <StatusBanner
        expanded={expanded}
        onClick={onClick}
        variant={BannerVariant.Warning}
      >
        {icons?.map((icon) => (
          <MediaIcon
            className='status__content__spoiler-icon'
            icon={icon}
            key={`icon-${icon}`}
          />
        ))}
        <span>{intl.formatMessage(messages.mfmPhotosensitiveWarning)}</span>
      </StatusBanner>
    );
  }

  if (!hasSpoiler) {
    return null;
  }

  const text =
    status.getIn(['translation', 'spoilerHtml']) || status.get('spoilerHtml');
  if (typeof text !== 'string' || text.length === 0) {
    return null;
  }

  return (
    <StatusBanner
      expanded={expanded}
      onClick={onClick}
      variant={BannerVariant.Warning}
    >
      {icons?.map((icon) => (
        <MediaIcon
          className='status__content__spoiler-icon'
          icon={icon}
          key={`icon-${icon}`}
        />
      ))}
      <EmojiHTML
        as='span'
        htmlString={text}
        extraEmojis={status.get('emojis') as List<CustomEmoji>}
      />
    </StatusBanner>
  );
};
